import { useEffect, useState } from "react";
import Papa from "papaparse";
import { cleanDataset } from "../utils/cleanData";

const SAMPLE_DATA = [
  {
    name: "AdBlock",
    users: 10000000,
    category: "Productivity",
    rating: 4.5,
    rating_count: 200000,
    subcategory: "Ad Blockers",
  },
  {
    name: "Grammarly",
    users: 10000000,
    category: "Productivity",
    rating: 4.6,
    rating_count: 150000,
    subcategory: "Writing",
  },
  {
    name: "Google Translate",
    users: 10000000,
    category: "Productivity",
    rating: 4.4,
    rating_count: 100000,
    subcategory: "Translation",
  },
  {
    name: "Honey",
    users: 10000000,
    category: "Shopping",
    rating: 4.8,
    rating_count: 180000,
    subcategory: "Coupons",
  },
  {
    name: "Zoom",
    users: 8000000,
    category: "Communication",
    rating: 4.2,
    rating_count: 50000,
    subcategory: "Video Conferencing",
  },
  {
    name: "LastPass",
    users: 7000000,
    category: "Productivity",
    rating: 4.5,
    rating_count: 80000,
    subcategory: "Password Manager",
  },
  {
    name: "Cisco Webex",
    users: 6000000,
    category: "Communication",
    rating: 4.1,
    rating_count: 40000,
    subcategory: "Video Conferencing",
  },
  {
    name: "Pinterest",
    users: 6000000,
    category: "Social",
    rating: 4.7,
    rating_count: 90000,
    subcategory: "Bookmarking",
  },
  {
    name: "Amazon Assistant",
    users: 5000000,
    category: "Shopping",
    rating: 4.0,
    rating_count: 30000,
    subcategory: "Price Tracker",
  },
  {
    name: "ColorZilla",
    users: 3000000,
    category: "Developer Tools",
    rating: 4.4,
    rating_count: 20000,
    subcategory: "Design",
  },
  {
    name: "Momentum",
    users: 2500000,
    category: "Productivity",
    rating: 4.5,
    rating_count: 12000,
    subcategory: "New Tab",
  },
  {
    name: "Dark Reader",
    users: 4000000,
    category: "Accessibility",
    rating: 4.7,
    rating_count: 15000,
    subcategory: "Themes",
  },
];

export default function useDataset() {
  const [data, setData] = useState([]);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  useEffect(() => {
    const loadFallback = () => {
      console.warn("Using fallback data due to fetch/parse error.");
      const processed = processCharts(SAMPLE_DATA);
      setCharts(processed);
      setData(SAMPLE_DATA);
      setLoading(false);
    };

    async function fetchData() {
      try {
        console.log("Fetching CSV started...");
        const response = await fetch("/top-chrome-extensions-data.csv");

        console.log("Fetch response status:", response.status);
        if (!response.ok) {
          loadFallback();
          return;
        }

        const text = await response.text();
        console.log("CSV text length:", text.length);

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          complete: (result) => {
            console.log("Parsed rows:", result.data.length);
            if (result.data.length > 0)
              console.log("Parsed sample:", result.data[0]);

            let cleaned = [];
            try {
              const resultData = cleanDataset(result.data);
              if (Array.isArray(resultData)) {
                cleaned = resultData;
              }
              console.log("Cleaned rows:", cleaned.length);
              if (cleaned.length > 0)
                console.log("Cleaned sample:", cleaned[0]);
            } catch (err) {
              console.error("Error cleaning data:", err);
              loadFallback();
              return;
            }

            try {
              const processedCharts = processCharts(cleaned);
              setCharts(processedCharts);
              setData(cleaned);
              setLoading(false);
            } catch (err) {
              console.error("Error processing charts:", err);
              loadFallback();
            }
          },
          error: (err) => {
            console.error("Papa parse error:", err);
            loadFallback();
          },
        });
      } catch (err) {
        console.error("Fetch error:", err);
        loadFallback();
      }
    }

    fetchData();
  }, []);

  return { data, charts, loading, error };
}

const processCharts = (data) => {
  if (!data || data.length === 0) return null;

  const parseNum = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === "string") {
      return parseFloat(val.replace(/,/g, "")) || 0;
    }
    return Number(val) || 0;
  };

  // 1. Top 10 Extensions by Users
  const topExtensions = [...data]
    .sort((a, b) => parseNum(b.users) - parseNum(a.users))
    .slice(0, 10)
    .map((d) => ({ name: d.name, users: parseNum(d.users) }));

  const chart1 = {
    data: topExtensions,
    insight:
      topExtensions.length > 0
        ? `Dominant extension: ${topExtensions[0].name} with ${topExtensions[0].users.toLocaleString()} users. Shows market leadership.`
        : "No data available.",
  };

  // 2. Category Market Share
  const catCounts = {};
  data.forEach((d) => {
    const cat = d.category || "Uncategorized";
    catCounts[cat] = (catCounts[cat] || 0) + 1;
  });
  const categoryData = Object.entries(catCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const chart2 = {
    data: categoryData,
    insight:
      categoryData.length > 0
        ? `${categoryData[0].name} dominates with ${((categoryData[0].value / data.length) * 100).toFixed(1)}% market share. Shows ecosystem distribution.`
        : "No data available.",
  };

  // 3. Average Rating by Category
  const catRatings = {};
  data.forEach((d) => {
    const cat = d.category || "Uncategorized";
    if (!catRatings[cat]) catRatings[cat] = { sum: 0, count: 0 };
    catRatings[cat].sum += parseNum(d.rating);
    catRatings[cat].count += 1;
  });
  const avgRatingData = Object.entries(catRatings)
    .map(([category, { sum, count }]) => ({
      category,
      rating: parseFloat((sum / count).toFixed(2)),
    }))
    .sort((a, b) => b.rating - a.rating);

  const chart3 = {
    data: avgRatingData,
    insight:
      avgRatingData.length > 0
        ? `${avgRatingData[0].category} maintains the highest quality with ${avgRatingData[0].rating} avg rating. Shows quality perception.`
        : "No data available.",
  };

  // 4. Users vs Rating Relationship
  const scatterData = data
    .map((d) => ({ x: parseNum(d.rating), y: parseNum(d.users), name: d.name }))
    .filter((d) => d.y > 0);

  const chart4 = {
    data: scatterData,
    insight:
      "Correlation analysis: Do high ratings guarantee more users? Detect viral vs niche extensions.",
  };

  // 5. Top Subcategories
  const subCatCounts = {};
  data.forEach((d) => {
    const sub = d.subcategory || d.category || "Unknown";
    subCatCounts[sub] = (subCatCounts[sub] || 0) + 1;
  });
  const subCatData = Object.entries(subCatCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const chart5 = {
    data: subCatData,
    insight:
      subCatData.length > 0
        ? `${subCatData[0].name} is the most crowded sub-domain. Shows micro-level trends.`
        : "No data available.",
  };

  // 6. Rank Distribution
  const rankFreq = {};
  data.forEach((d) => {
    if (d.rank) {
      const r = Math.floor(parseNum(d.rank) / 100) * 100;
      rankFreq[r] = (rankFreq[r] || 0) + 1;
    }
  });
  const rankData = Object.entries(rankFreq)
    .map(([rank, frequency]) => ({ rank: Number(rank), frequency }))
    .sort((a, b) => a.rank - b.rank);

  const chart6 = {
    data: rankData,
    insight: "Ranking health analysis: How competitive subcategories are.",
  };

  // 7. Rating Count vs Users
  const bubbleData = data
    .map((d) => ({
      x: parseNum(d.rating_count),
      y: parseNum(d.users),
      z: parseNum(d.rating),
      name: d.name,
    }))
    .filter((d) => d.y > 0 && d.x > 0);

  const chart7 = {
    data: bubbleData,
    insight:
      "Trust vs popularity analysis: Higher rating counts often correlate with user trust.",
  };

  // 8. Category vs Rating Distribution (Stacked Bar approximation)
  const ratingBuckets = {};
  data.forEach((d) => {
    const cat = d.category || "Uncategorized";
    const rating = parseNum(d.rating);
    if (!ratingBuckets[cat]) {
      ratingBuckets[cat] = { low: 0, mid: 0, high: 0, excellent: 0 };
    }
    if (rating < 3.5) ratingBuckets[cat].low++;
    else if (rating < 4.2) ratingBuckets[cat].mid++;
    else if (rating < 4.7) ratingBuckets[cat].high++;
    else ratingBuckets[cat].excellent++;
  });

  const chart8Data = Object.entries(ratingBuckets)
    .map(([name, buckets]) => ({
      name,
      ...buckets,
      total: buckets.low + buckets.mid + buckets.high + buckets.excellent,
    }))
    .sort((a, b) => b.total - a.total);

  const chart8 = {
    data: chart8Data,
    insight:
      "Quality spread: 'Excellent' (>4.7) vs 'Low' (<3.5) ratings across categories.",
  };

  // 9. Total Users by Category
  const catUsers = {};
  data.forEach((d) => {
    const cat = d.category || "Uncategorized";
    catUsers[cat] = (catUsers[cat] || 0) + parseNum(d.users);
  });
  const chart9Data = Object.entries(catUsers)
    .map(([name, users]) => ({ name, users }))
    .sort((a, b) => b.users - a.users);

  const chart9 = {
    data: chart9Data,
    insight:
      chart9Data.length > 0
        ? `${chart9Data[0].name} dominates with ${(
            chart9Data[0].users / 1_000_000
          ).toFixed(1)}M+ users.`
        : "No data.",
  };

  // 10. Underrated Extensions Detection
  const chart10Data = data
    .map((d) => ({
      x: parseNum(d.rating),
      y: parseNum(d.users),
      name: d.name,
      category: d.category,
    }))
    .filter((d) => d.y > 0 && d.x > 0);

  const chart10 = {
    data: chart10Data,
    insight:
      "Gems found in the bottom-right quadrant (High Rating, Low Users).",
  };

  // 11. Extensions Added Over Time
  const timeline = {};
  data.forEach((d) => {
    if (d.enriched_at) {
      const date = new Date(d.enriched_at);
      if (!isNaN(date.getTime())) {
        const key = date.toISOString().slice(0, 7); // YYYY-MM
        timeline[key] = (timeline[key] || 0) + 1;
      }
    }
  });

  let chart11Data = Object.entries(timeline)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));

  let cumulative = 0;
  chart11Data = chart11Data.map((d) => {
    cumulative += d.count;
    return { ...d, cumulative };
  });

  const chart11 = {
    data: chart11Data,
    insight: "Growth trend of the extension ecosystem over time.",
  };

  // 12. Top Extensions % Market Share (for Donut Chart)
  const topExtensionsForShare = [...data]
    .sort((a, b) => parseNum(b.users) - parseNum(a.users))
    .slice(0, 8)
    .map((d) => ({
      name: d.name,
      users: parseNum(d.users),
      marketShare: 0, // Will be calculated below
    }));

  const totalTopUsers = topExtensionsForShare.reduce(
    (sum, d) => sum + d.users,
    0,
  );
  topExtensionsForShare.forEach((d) => {
    d.marketShare = totalTopUsers > 0 ? (d.users / totalTopUsers) * 100 : 0;
  });

  const chart12 = {
    data: topExtensionsForShare,
    insight:
      topExtensionsForShare.length > 0
        ? `${topExtensionsForShare[0].name} dominates with ${topExtensionsForShare[0].marketShare.toFixed(1)}% of top extension market share.`
        : "No data available.",
  };

  // 13. Users Distribution (All Extensions) - for Histogram
  const userCounts = data
    .map((d) => parseNum(d.users))
    .filter((users) => users > 0);

  // Create bins for histogram
  const bins = [
    { range: "0-1K", min: 0, max: 1000, count: 0 },
    { range: "1K-10K", min: 1000, max: 10000, count: 0 },
    { range: "10K-100K", min: 10000, max: 100000, count: 0 },
    { range: "100K-1M", min: 100000, max: 1000000, count: 0 },
    { range: "1M-10M", min: 1000000, max: 10000000, count: 0 },
    { range: "10M+", min: 10000000, max: Infinity, count: 0 },
  ];

  userCounts.forEach((users) => {
    for (const bin of bins) {
      if (users >= bin.min && users < bin.max) {
        bin.count++;
        break;
      }
    }
  });

  const chart13 = {
    data: bins,
    insight: `Most extensions (${(((bins[0].count + bins[1].count) / data.length) * 100).toFixed(1)}%) have fewer than 10K users, showing a long-tail distribution.`,
  };

  // 14. Cumulative Users Contribution Curve (Pareto Chart)
  const sortedByUsers = [...data]
    .sort((a, b) => parseNum(b.users) - parseNum(a.users))
    .map((d) => ({ users: parseNum(d.users), name: d.name }));

  const totalAllUsers = sortedByUsers.reduce((sum, d) => sum + d.users, 0);
  let cumulativeUsers = 0;
  const paretoData = sortedByUsers.map((d, index) => {
    cumulativeUsers += d.users;
    const extensionCount = index + 1;
    const extensionPercent = (extensionCount / data.length) * 100;
    const usersPercent = (cumulativeUsers / totalAllUsers) * 100;

    return {
      extensionCount,
      extensionPercent: extensionPercent.toFixed(2),
      usersPercent: usersPercent.toFixed(2),
      users: cumulativeUsers,
      extensionName: d.name,
    };
  });

  // Sample every 10th point for better visualization
  const sampledParetoData = paretoData.filter(
    (_, index) => index % Math.max(1, Math.floor(paretoData.length / 50)) === 0,
  );

  const chart14 = {
    data: sampledParetoData,
    insight: `Top 20% of extensions capture approximately ${paretoData[Math.floor(data.length * 0.2) - 1]?.usersPercent || "N/A"}% of total users, following the Pareto principle.`,
  };

  // 8. KPI Summary Cards
  const kpi = {
    totalExtensions: data.length,
    totalCategories: Object.keys(catCounts).length,
    avgRating: (
      data.reduce((acc, d) => acc + parseNum(d.rating), 0) / data.length
    ).toFixed(2),
    totalUsers: data
      .reduce((acc, d) => acc + parseNum(d.users), 0)
      .toLocaleString(),
  };

  return {
    chart1,
    chart2,
    chart3,
    chart4,
    chart5,
    chart6,
    chart7,
    chart8,
    chart9,
    chart10,
    chart11,
    chart12,
    chart13,
    chart14,
    kpi,
  };
};
